import React, { useContext } from 'react';
import { Query } from 'react-apollo';
import PropTypes from 'prop-types';
import { Button } from '@sm/wds-react';
import { createURL } from '@sm/utils';
import { T, defineMessages } from '@sm/intl';
import { generateMetricsAttribute } from '@sm/metrics';
import { TabletScreen } from '../../../MediaComponents';
import LogInCTA from '../LogInCTA';
import SignUpCTA from '../SignUpCTA';
import SMHeaderContext from '../../SMHeaderContext';

import addonQuery from './Addon.graphql';

import './addon.scss';

const COPY = defineMessages({
  UPGRADE: {
    id: 'Addon.upgradeButton',
    defaultMessage: 'UPGRADE',
    description:
      '[Type: Label][Vis.: high] - This is the upgrade addon button on the header.',
  },
  CREATE_SURVEY: {
    id: 'Addon.createSurvey',
    defaultMessage: 'CREATE SURVEY',
    description:
      '[Type: Label][Vis.: high] - This is the create survey addon button on the header.',
  },
  CREATE_TEAM: {
    id: 'Addon.createTeam',
    defaultMessage: 'CREATE TEAM',
    description:
      '[Type: Label][Vis.: high] - This is the create team addon button on the header.',
  },
  ADD_USERS: {
    id: 'Addon.addUsers',
    defaultMessage: 'ADD USERS',
    description:
      '[Type: Label][Vis.: high] - This is the add users addon button on the header.',
  },
});

export const UpgradeButton = () => {
  const smHeaderContext = useContext(SMHeaderContext);
  return (
    <Button
      color="upgrade"
      size="sm"
      data-sm-metrics={generateMetricsAttribute({
        data: {
          actionType: 'webplat_header_upgrade.button_press',
          actionFlow: smHeaderContext.actionFlow,
        },
      })}
      href={createURL('/pricing/upgrade/', {
        ut_source: 'header_upgrade',
        ut_source3: 'webplat',
      })}
    >
      <T desc={COPY.UPGRADE} />
    </Button>
  );
};

export const CreateButton = () => {
  return (
    <Button
      data-testid="Addon__CreateSurveyBtn"
      color="alt"
      className="wds-m-l-3"
      size="sm"
      href={createURL('/create/', { ut_source: 'header' })}
    >
      <T desc={COPY.CREATE_SURVEY} />
    </Button>
  );
};

export const CreateTeam = () => {
  return (
    <Button
      data-testid="Addon__CreateTeamBtn"
      color="upgrade"
      size="sm"
      href={createURL('/billing/p2gupgrade/', {
        ut_source: 'header',
        ut_source3: 'webplat',
      })}
    >
      <T desc={COPY.CREATE_TEAM} />
    </Button>
  );
};

export const AddUsers = () => {
  return (
    <Button
      data-testid="Addon__AddUsersBtn"
      color="upgrade"
      size="sm"
      href={createURL('/team/add/', {
        ut_source: 'header',
        ut_source3: 'webplat',
      })}
    >
      <T desc={COPY.ADD_USERS} />
    </Button>
  );
};

export const showLICTA = data => {
  const user = data.user || {};
  const { team } = user;
  const teamMembership = user.teamMembership || {};
  const { isAnnual } = user.package || {};
  const canAddUsers =
    teamMembership.isAdmin && team && team.ssoState !== 'REQUIRED';

  if (canAddUsers) {
    return <AddUsers />;
  }
  if (!team && isAnnual) {
    return <CreateTeam />;
  }
  if (!team) {
    return <UpgradeButton />;
  }
  return null;
};
export const Addon = ({
  isUserAuthenticated,
  showCreateSurveyButton,
  addonLogInColor,
  addonSignUpColor,
  addonSignUpVariant,
  variant,
  showLoginButton,
  showSignupButton,
}) => {
  const { isMRX } = useContext(SMHeaderContext);
  const auth = isUserAuthenticated ? 'auth' : 'anon';
  return (
    <TabletScreen>
      <div
        data-testid="Addon__Container"
        className={`sm-addon__${auth}--${variant}`}
      >
        <Choose>
          <When condition={!(isUserAuthenticated || isMRX)}>
            <>
              <If condition={showLoginButton}>
                <LogInCTA
                  variant="text"
                  color={addonLogInColor}
                  params={{ ut_source: 'megamenu' }}
                  className={`sm-addon__log-in sm-addon__log-in--${addonLogInColor}`}
                />
              </If>
              <If condition={showSignupButton}>
                <SignUpCTA
                  variant={addonSignUpVariant}
                  color={addonSignUpColor}
                  params={{ ut_source: 'megamenu' }}
                  className={`sm-addon__sign-up sm-addon__sign-up--${addonSignUpColor}`}
                />
              </If>
            </>
          </When>
          <Otherwise>
            <Query query={addonQuery}>
              {({ loading, error, data }) => {
                if (loading) return null;
                if (error) return null;
                return (
                  <>
                    {showLICTA(data)}
                    <If condition={showCreateSurveyButton}>
                      <CreateButton />
                    </If>
                  </>
                );
              }}
            </Query>
          </Otherwise>
        </Choose>
      </div>
    </TabletScreen>
  );
};

Addon.propTypes = {
  isUserAuthenticated: PropTypes.bool,
  showCreateSurveyButton: PropTypes.bool,
  addonLogInColor: PropTypes.oneOf(['secondary', 'alt']),
  addonSignUpColor: PropTypes.oneOf(['upgrade', 'alt']),
  addonSignUpVariant: PropTypes.oneOf(['default', 'ghost']),
  variant: PropTypes.oneOf(['default', 'limited', 'slim']),
  showLoginButton: PropTypes.bool,
  showSignupButton: PropTypes.bool,
};

Addon.defaultProps = {
  isUserAuthenticated: false,
  showCreateSurveyButton: true,
  addonLogInColor: 'alt',
  addonSignUpColor: 'alt',
  addonSignUpVariant: 'default',
  variant: 'default',
  showLoginButton: true,
  showSignupButton: true,
};
